import { extractText } from "../chat/message-extract.ts";
import type { FleetWorkPlanRecord, FleetWorkUnit } from "../types.ts";
import { inferFleetDisplayRole } from "./fleet-plan-roles.ts";

export type FleetSessionLogLine = { atMs?: number; text: string };

const MAX_LINES = 40;
const MAX_LINE_CHARS = 280;

/**
 * Turn a `chat.history` payload into timestamped log lines for the
 * Implementation output panel. User/system rows are the spawn prompt, not
 * worker output.
 */
export function transcriptToLogLines(messages: unknown): FleetSessionLogLine[] {
  if (!Array.isArray(messages)) {
    return [];
  }
  const lines: FleetSessionLogLine[] = [];
  for (const raw of messages) {
    if (!raw || typeof raw !== "object") {
      continue;
    }
    const msg = raw as Record<string, unknown>;
    const role = typeof msg.role === "string" ? msg.role.toLowerCase() : "";
    if (role === "user" || role === "system") {
      continue;
    }
    const text = (extractText(msg) ?? "").trim();
    if (!text) {
      continue;
    }
    const timestamp = msg.timestamp;
    const atMs =
      typeof timestamp === "number" && Number.isFinite(timestamp) && timestamp > 0
        ? timestamp
        : undefined;
    for (const piece of text.split(/\n+/)) {
      const clipped = piece.trim().slice(0, MAX_LINE_CHARS);
      if (clipped) {
        lines.push({ atMs, text: clipped });
      }
    }
  }
  return lines.length > MAX_LINES ? lines.slice(-MAX_LINES) : lines;
}

/** Child sessions whose transcripts should be polled for the open cards. */
export function childSessionKeysToWatch(
  plan: FleetWorkPlanRecord | null,
  expandedRoleIds: Set<string>,
): string[] {
  if (!plan || expandedRoleIds.size === 0) {
    return [];
  }
  const keys = new Set<string>();
  for (const unit of plan.units) {
    const key = unit.childSessionKey?.trim();
    if (!key) {
      continue;
    }
    const role = inferFleetDisplayRole({
      ...unit,
      plannerAgentId: plan.plannerAgentId,
    });
    if (expandedRoleIds.has(role.roleId)) {
      keys.add(key);
    }
  }
  return [...keys];
}

export function mergeSessionLogsForUnits(
  units: readonly FleetWorkUnit[],
  logsBySession?: Record<string, FleetSessionLogLine[]>,
): FleetSessionLogLine[] {
  if (!logsBySession) {
    return [];
  }
  const merged: FleetSessionLogLine[] = [];
  const seen = new Set<string>();
  for (const unit of units) {
    const key = unit.childSessionKey?.trim();
    if (!key || seen.has(key)) {
      continue;
    }
    seen.add(key);
    const lines = logsBySession[key];
    if (Array.isArray(lines) && lines.length > 0) {
      merged.push(...lines);
    }
  }
  merged.sort((a, b) => (a.atMs ?? 0) - (b.atMs ?? 0));
  return merged.length > MAX_LINES ? merged.slice(-MAX_LINES) : merged;
}
